import './App.css';
import { ThemeProvider } from '@mui/material/styles';
import {ArticlesPage} from "./pages/ArticlesPage";
import {Suspense, lazy, useEffect, useMemo, useState} from "react";
import {Box, createTheme, CssBaseline, GlobalStyles} from "@mui/material";
import {flushSync} from "react-dom";
import {Sidebar, MobileTopBar} from "./components/Sidebar";
import Footer from "./components/Footer";
import {BrowserRouter, Navigate, Route, Routes, useLocation} from "react-router-dom";
import {ArticlesDetailsPage} from "./pages/ArticlesDetailsPage";
import TagDetailsPage from "./pages/TagDetailsPage";
import UserProfilePage from "./pages/UserProfilePage";
import CreateArticlePage from "./pages/CreateArticlePage";
import SignInPage from "./pages/SignInPage";
import {ProblemCodeDetailsPage} from "./pages/ProblemCodeDetailsPage";
import {ProfilePage} from "./pages/ProfilePage";
import {GroupsPage} from "./pages/GroupsPage";
import {GroupDetailsPage} from "./pages/GroupDetailsPage";
import AutoGradeIssuesPage from "./pages/AutoGradeIssuesPage";
import {ExamLockBanner, ExamLockGate} from "./components/ExamLock";
import {AdminLabsPage} from "./pages/AdminLabsPage";
import AppealsPage from "./pages/AppealsPage";
import AdminAppealsPage from "./pages/AdminAppealsPage";
import {AdminGitTasksPage} from "./pages/AdminGitTasksPage";
import {AdminMysqlTasksPage} from "./pages/AdminMysqlTasksPage";
import AdminCriteriaGradingPage from "./pages/AdminCriteriaGradingPage";
import CriteriaPage from "./pages/CriteriaPage";
import NotificationsPage from "./pages/NotificationsPage";
import AdminMessengersPage from "./pages/AdminMessengersPage";
import RequireAuth from "./components/RequireAuth";
// Диалог подключения Telegram пока отключён — вместо него показываем диалог про допуск к экзамену (вернуть: <TelegramConnectDialog/>).
// import {TelegramConnectDialog} from "./components/TelegramConnect";
import ExamAdmissionDialog from "./components/ExamAdmissionDialog";
import {PrivacyPage} from "./pages/PrivacyPage";
import {PrivacyPolicyPage} from "./pages/PrivacyPolicyPage";
import {AdminCourseworksPage} from "./pages/AdminCourseworksPage";
import {CourseworkDetailsPage} from "./pages/CourseworkDetailsPage";
import AdminExamPage from "./pages/AdminExamPage";
import AdminExamsPage from "./pages/AdminExamsPage";
import AdminExamEventPage from "./pages/AdminExamEventPage";
import ExamAttemptPage from "./pages/ExamAttemptPage";
import ExamsPage from "./pages/ExamsPage";
import AdminSchedulePage from "./pages/AdminSchedulePage";
import SchedulePage from "./pages/SchedulePage";
import AdminAsursoPage from "./pages/AdminAsursoPage";
import ExamOverviewPage from "./pages/ExamOverviewPage";
import YandexAdBlock from "./components/ads/YandexAdBlock";
import FloorAdBlock from "./components/ads/FloorAdBlock";
import InImageAdInjector from "./components/ads/InImageAdInjector";
import {showOverlayAd} from "./components/ads/showOverlayAd";
import ConfirmHost from "./components/ConfirmHost";

// Страница с графиками (recharts) грузится отдельным куском — основной бандл не раздуваем.
const AdminLlmPage = lazy(() => import("./pages/AdminLlmPage"));

const TOP_BANNER_BLOCK_ID = "R-A-20141312-3";

const getDesignTokens = (mode) => ({
    palette: {
        mode,
        ...(mode === 'light'
            ? {
                // Светлая тема (Modern Soft UI)
                primary: {
                    main: '#4361ee',  // Яркий кобальтовый синий
                    light: '#4895ef', // Электрический голубой
                },
                secondary: {
                    main: '#f72585',  // Неоновый пурпурный
                },
                background: {
                    default: '#f0f2f5',  // Светло-серый с голубым оттенком
                    paper: '#ffffff',   // Чистый белый
                },
                text: {
                    primary: '#2b2d42', // Темно-сине-серый
                    secondary: '#8d99ae', // Мягкий серо-голубой
                }
            }
            : {
                // Тёмная тема (Deep Space)
                primary: {
                    main: '#5e60ce',   // Фиолетово-синий (Neon)
                    light: '#5390d9',  // Сияющий сапфировый
                },
                secondary: {
                    main: '#80ffdb',  // Криптоново-бирюзовый
                },
                background: {
                    default: '#121826',  // Глубокий сине-черный
                    paper: '#1e293b',   // Углубленный темно-синий
                },
                text: {
                    primary: '#e2e8f0', // Светло-серебристый
                    secondary: '#94a3b8', // Дымчато-голубой
                }
            }),
    },
    typography: {
        fontFamily: 'Inter, sans-serif', // Inter - современный шрифт
        h5: {
            fontWeight: 700,           // Более жирное начертание
            letterSpacing: '-0.015em'  // Узкий кернинг
        },
        body1: {
            lineHeight: 1.7            // Улучшенная читаемость
        }
    },
    shape: {
        borderRadius: 6,             // Лёгкое скругление (sx borderRadius: 2 → 12px)
    },
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: mode === 'light'
                        ? '0px 6px 24px rgba(149, 157, 165, 0.15)' // Мягкая тень (свет)
                        : '0px 8px 30px rgba(0, 0, 0, 0.35)',      // Глубокая тень (темная)
                    backdropFilter: 'blur(12px)',                 // Эффект матового стекла
                    background: mode === 'light'
                        ? 'rgba(255, 255, 255, 0.85)'              // Легкая прозрачность
                        : 'rgba(30, 41, 59, 0.7)',                 // Глубокий стеклянный эффект
                    border: mode === 'light'
                        ? '1px solid rgba(0, 0, 0, 0.05)'          // Тонкая граница (свет)
                        : '1px solid rgba(255, 255, 255, 0.05)'    // Тонкая граница (темно)
                },
            },
        },
        // Диалоги: сплошная поверхность без «серого налёта» (MUI подмешивает светлый градиент на высоких elevation),
        // крупное скругление, тонкая рамка, мягкая тень и размытый фон за окном.
        MuiDialog: {
            styleOverrides: {
                root: {
                    '& .MuiBackdrop-root': {
                        backgroundColor: mode === 'light' ? 'rgba(15, 23, 42, 0.35)' : 'rgba(5, 8, 18, 0.65)',
                        backdropFilter: 'blur(4px)',
                    },
                },
                paper: {
                    backgroundImage: 'none',
                    backgroundColor: mode === 'light' ? '#ffffff' : '#172033',
                    borderRadius: 20,
                    border: mode === 'light' ? '1px solid rgba(15, 23, 42, 0.08)' : '1px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: mode === 'light'
                        ? '0 24px 64px rgba(15, 23, 42, 0.22)'
                        : '0 24px 64px rgba(0, 0, 0, 0.6)',
                    // На телефоне окно не прилипает к краям экрана.
                    '@media (max-width:600px)': {margin: 16, width: 'calc(100% - 32px)', maxHeight: 'calc(100% - 32px)'},
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.01em', padding: '24px 24px 8px'},
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {padding: '12px 24px', '.MuiDialogTitle-root + &': {paddingTop: 12}},
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '12px 24px 24px',
                    gap: 8,
                    '& > :not(style) ~ :not(style)': {marginLeft: 0},
                    '& .MuiButton-root': {padding: '8px 20px', borderRadius: 10},
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {root: {borderRadius: 10}},
        },
        // Выпадающие списки и меню в том же стиле.
        MuiPopover: {
            styleOverrides: {
                paper: {
                    backgroundImage: 'none',
                    backgroundColor: mode === 'light' ? '#ffffff' : '#172033',
                    borderRadius: 12,
                    border: mode === 'light' ? '1px solid rgba(15, 23, 42, 0.08)' : '1px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: mode === 'light' ? '0 12px 32px rgba(15, 23, 42, 0.18)' : '0 12px 32px rgba(0, 0, 0, 0.5)',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',          // Без CAPS
                    fontWeight: 600,
                    padding: '10px 24px',
                },
                // Маленькие кнопки (панели действий, строки списков) — компактные, а не с отступами обычной кнопки.
                sizeSmall: {
                    padding: '5px 12px',
                    fontSize: '0.8125rem',
                },
                contained: {
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: mode === 'light'
                            ? '0 4px 12px rgba(67, 97, 238, 0.3)'
                            : '0 4px 16px rgba(128, 255, 219, 0.4)'
                    }
                }
            }
        }
    },
    // Дополнительные глобальные эффекты
    shadows: mode === 'light'
        ? ['none', '0 2px 8px rgba(0,0,0,0.05)', ...Array(23).fill('none')]
        : ['none', '0 4px 16px rgba(0,0,0,0.4)', ...Array(23).fill('none')]
});

export const getTheme = (mode) => createTheme(getDesignTokens(mode));

function App() {
    const [mode, setMode] = useState(localStorage.getItem('theme_mode') ? localStorage.getItem('theme_mode') : "dark");
    const theme = useMemo(() => getTheme(mode), [mode]);

    useEffect(() => {
        localStorage.setItem("theme_mode", mode)
    }, [mode])

    // Смена темы без перезагрузки страницы. В браузерах с View Transitions новая тема «разливается» кругом из места клика;
    // в остальных — плавное затухание цветов (класс theme-fade на <html>). При prefers-reduced-motion — без анимации.
    const toggleTheme = (event) => {
        const next = mode === 'light' ? 'dark' : 'light';
        const apply = () => flushSync(() => setMode(next));
        const root = document.documentElement;
        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reduce) {
            apply();
            return;
        }
        if (!document.startViewTransition) {
            root.classList.add('theme-fade');
            apply();
            setTimeout(() => root.classList.remove('theme-fade'), 500);
            return;
        }
        const x = event?.clientX ?? window.innerWidth / 2;
        const y = event?.clientY ?? window.innerHeight / 2;
        const radius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));
        const transition = document.startViewTransition(apply);
        transition.ready.then(() => {
            root.animate(
                {clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`]},
                {duration: 550, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', pseudoElement: '::view-transition-new(root)'}
            );
        }).catch(() => {});
    };

    return (
        <BrowserRouter>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <GlobalStyles styles={{
                    // Анимация смены темы (View Transitions API): новый вид раскрывается поверх старого.
                    '::view-transition-old(root), ::view-transition-new(root)': {animation: 'none', mixBlendMode: 'normal'},
                    '::view-transition-old(root)': {zIndex: 1},
                    '::view-transition-new(root)': {zIndex: 2147483646},
                    // Запасной вариант без View Transitions: плавное изменение цветов.
                    'html.theme-fade *, html.theme-fade *::before, html.theme-fade *::after': {
                        transition: 'background-color .4s ease, color .4s ease, border-color .4s ease, fill .4s ease, box-shadow .4s ease !important',
                    },
                }}/>
                <AppLayout mode={mode} toggleTheme={toggleTheme} />
                <ConfirmHost />
            </ThemeProvider>
        </BrowserRouter>
    );
}

const NO_FOOTER_PATH_PREFIXES = ['/problems/'];

const AppLayout = ({ mode, toggleTheme }) => {
    const location = useLocation();
    const hideFooter = NO_FOOTER_PATH_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Overlay-β не перекрывает контент целиком (в отличие от Полноэкранного),
    // поэтому его уместно триггерить на каждый переход по сайту, а не только
    // на конкретное действие — лимит показов всё равно регулируется в кабинете.
    useEffect(() => {
        showOverlayAd();
    }, [location.pathname]);

    return (
        <Box display="flex" minHeight="100vh">
            <Sidebar mode={mode} toggleTheme={toggleTheme} mobileOpen={mobileMenuOpen} setMobileOpen={setMobileMenuOpen}/>

            <Box component="main" display="flex" flexDirection="column" flexGrow={1} minWidth={0}>
            <MobileTopBar onMenu={() => setMobileMenuOpen(true)}/>

            <Box sx={{maxWidth: 'lg', mx: 'auto', width: '100%', px: 2, pt: 2}}>
                <YandexAdBlock blockId={TOP_BANNER_BLOCK_ID} collapseWhenEmpty/>
            </Box>

            {/* Floor Ad — липкий рекламный блок снизу экрана, рендерится Яндексом
                поверх контента (fixed-overlay). На коротких страницах без футера
                (например /sign-in) он перекрывал интерактивные элементы формы —
                см. найденный вживую баг: клик по полю пароля попадал в рекламу.
                Запас снизу не даёт последнему видимому элементу страницы упереться
                в зону, где появляется Floor Ad. */}
            <Box flexGrow={1} sx={{pb: 12}}>
                <ExamLockBanner/>
                <Routes>
                    <Route path="/articles" element={<ExamLockGate blockParticipants><ArticlesPage /></ExamLockGate>} />
                    <Route path="/articles/:id" element={<ExamLockGate blockParticipants><ArticlesDetailsPage mode={mode}/></ExamLockGate>} />
                    <Route path="/problems/:problemId" element={<ExamLockGate><ProblemCodeDetailsPage mode={mode}/></ExamLockGate>} />
                    <Route path="/problems/:problemId/criteria" element={<RequireAuth><CriteriaPage /></RequireAuth>} />
                    <Route path="/articles/new" element={<CreateArticlePage />} />
                    <Route path="/articles/edit/:id" element={<CreateArticlePage />} />
                    <Route path="/tags/:id" element={<ExamLockGate blockParticipants><TagDetailsPage /></ExamLockGate>} />
                    <Route path="/users/:id" element={<ExamLockGate blockParticipants><UserProfilePage /></ExamLockGate>} />
                    <Route path="/sign-in" element={<SignInPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/groups" element={<GroupsPage />} />
                    <Route path="/groups/auto-grades" element={<AutoGradeIssuesPage />} />
                    <Route path="/groups/:id" element={<GroupDetailsPage />} />
                    <Route path="/appeals" element={<RequireAuth><AppealsPage /></RequireAuth>} />
                    <Route path="/admin/appeals" element={<RequireAuth><AdminAppealsPage /></RequireAuth>} />
                    <Route path="/admin/labs" element={<RequireAuth><AdminLabsPage /></RequireAuth>} />
                    <Route path="/admin/git-tasks" element={<RequireAuth><AdminGitTasksPage /></RequireAuth>} />
                    <Route path="/admin/mysql-tasks" element={<RequireAuth><AdminMysqlTasksPage /></RequireAuth>} />
                    <Route path="/admin/llm" element={<RequireAuth><Suspense fallback={null}><AdminLlmPage /></Suspense></RequireAuth>} />
                    <Route path="/admin/problems/:problemId/grading" element={<RequireAuth><AdminCriteriaGradingPage /></RequireAuth>} />
                    <Route path="/notifications" element={<RequireAuth><NotificationsPage /></RequireAuth>} />
                    <Route path="/admin/messengers" element={<RequireAuth><AdminMessengersPage /></RequireAuth>} />
                    <Route path="/admin/max" element={<Navigate to="/admin/messengers?tab=groups" replace />} />
                    <Route path="/admin/chats" element={<Navigate to="/admin/messengers?tab=groups" replace />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="/admin/courseworks" element={<RequireAuth><AdminCourseworksPage /></RequireAuth>} />
                    <Route path="/admin/exams" element={<RequireAuth><AdminExamsPage /></RequireAuth>} />
                    <Route path="/admin/exams/:tagId" element={<RequireAuth><AdminExamPage /></RequireAuth>} />
                    <Route path="/admin/exam-events/:id" element={<RequireAuth><AdminExamEventPage /></RequireAuth>} />
                    <Route path="/schedule" element={<RequireAuth><SchedulePage /></RequireAuth>} />
                    <Route path="/admin/asurso" element={<RequireAuth><AdminAsursoPage /></RequireAuth>} />
                    <Route path="/admin/schedule" element={<RequireAuth><AdminSchedulePage /></RequireAuth>} />
                    <Route path="/exams" element={<RequireAuth><ExamsPage /></RequireAuth>} />
                    <Route path="/exams/event/:eventId" element={<RequireAuth><ExamOverviewPage /></RequireAuth>} />
                    <Route path="/exams/attempts/:id" element={<RequireAuth><ExamAttemptPage /></RequireAuth>} />
                    <Route path="/admin/courseworks/:id" element={<RequireAuth><CourseworkDetailsPage /></RequireAuth>} />
                    <Route path="*" element={<Navigate to="/articles" />} />
                </Routes>
            </Box>

            {!hideFooter && <Footer />}
            <ExamAdmissionDialog/>
            <FloorAdBlock/>
            <InImageAdInjector/>
            </Box>
        </Box>
    );
};

export default App;